/**
 * <TutorOfferCard> — une offre de cours dans la liste de l'onglet « Offres de cours » (organism).
 *
 * Legacy `ProposalsTabScreen` → `renderProposalItem`, à l'identique : la date de l'offre, son titre
 * (« 3 élèves - 4ème - Maths », « Nouvel élève 5ème - Maths »), ses créneaux (ou la période d'un
 * remplacement temporaire, ou la date d'un remplacement), et ses chips.
 *
 * - **En attente**, la carte porte le liseré de l'univers (une carte à distinguer sans être LA carte
 *   de la page) et se touche : elle ouvre l'offre (`<TutorOfferFlow>`). Une offre ciblée (le groupe
 *   tient un élève que le tuteur a déjà eu) porte « Proposition avec ancien élève » ; un changement de
 *   créneau (la famille d'un de ses élèves change d'horaire), « Ton élève ».
 * - **Candidatée**, elle se touche aussi : pour retirer la candidature. Sa chip dit quand.
 * - **Autrement** (retenu, non retenu, candidat en conflit, masquée, expirée), elle ne se touche pas
 *   et s'écrit à l'encre secondaire.
 * - Un remplacement temporaire porte « Temporaire ».
 * - Pendant qu'une réponse part, la place des chips dit « Envoi en cours… » (`<WaitingText>`) : la
 *   roue du legacy n'a pas de bouton pour la porter.
 */
import React from "react"
import { StyleSheet } from "react-native-unistyles"

import { Badge, type BadgeVariant } from "../../atoms/Badge"
import { Row } from "../../atoms/containers/Row"
import { Stack } from "../../atoms/containers/Stack"
import { Text } from "../../atoms/typography/Text"
import { WaitingText } from "../../atoms/WaitingText"
import { Card } from "../../molecules/Card"
import { TutorOfferLineText } from "../TutorOfferDetails"
import {
    type UITutorOffer,
    type UITutorOfferKind,
    type UITutorOfferStatus,
} from "../../../ports/primary/ui/types/tutorDispoOffres"

type StatusChip = { text: string; variant: BadgeVariant }

/** Les mots et les tons du legacy (`formConstants` → `…ProposalStatusNames` / `…Colors`). */
const GROUP_STATUS: Record<Exclude<UITutorOfferStatus, "pending">, StatusChip> = {
    accepted: { text: "Candidatée", variant: "info" },
    rejected: { text: "Masquée", variant: "neutral" },
    expired: { text: "Expirée", variant: "neutral" },
    chosen: { text: "Retenu", variant: "success" },
    not_chosen: { text: "Non retenu", variant: "danger" },
    accepted_canceled: { text: "Candidat (conflit)", variant: "warning" },
}

const SUBSTITUTION_STATUS: Record<Exclude<UITutorOfferStatus, "pending">, StatusChip> = {
    ...GROUP_STATUS,
    accepted: { text: "Acceptée", variant: "success" },
    rejected: { text: "Masquée", variant: "danger" },
}

const statusChip = (kind: UITutorOfferKind, status: Exclude<UITutorOfferStatus, "pending">) =>
    (kind === "substitution" ? SUBSTITUTION_STATUS : GROUP_STATUS)[status]

const TEXTS = {
    targeted: "Proposition avec ancien élève",
    ownStudent: "Ton élève",
    temporary: "Temporaire",
    sending: "Envoi en cours…",
}

/**
 * La chip qui dit à qui l'offre s'adresse, partagée avec l'accueil (`<TutorHomeOffer>`) : « Ton élève »
 * pour un changement de créneau (le serveur le marque toujours ciblé), « Proposition avec ancien
 * élève » pour une autre offre ciblée.
 */
export function tutorOfferAudienceBadge(offer: UITutorOffer): string | null {
    if (offer.slotChange != null) return TEXTS.ownStudent
    return offer.targeted === true ? TEXTS.targeted : null
}

export type TutorOfferCardProps = {
    offer: UITutorOffer
    /** Toucher une offre en attente (l'ouvrir) ou candidatée (retirer la candidature). */
    onPress?: (offer: UITutorOffer) => void
    /** La réponse du tuteur part. */
    sending?: boolean
    testID?: string
}

export function TutorOfferCard(props: TutorOfferCardProps) {
    const { offer, onPress, sending = false, testID } = props
    const pending = offer.status === "pending"
    const tappable = (pending || offer.status === "accepted") && !sending && onPress != null
    const audience = tutorOfferAudienceBadge(offer)

    const chips = sending ? (
        <WaitingText size="text-sm" align="start">
            {TEXTS.sending}
        </WaitingText>
    ) : (
        <>
            {pending && audience != null && <Badge text={audience} variant="info" tinted truncate={false} />}
            {offer.status !== "pending" && (
                <Badge
                    text={`${statusChip(offer.kind, offer.status).text}${
                        offer.status === "accepted" && offer.statusDateLabel != null
                            ? ` • ${offer.statusDateLabel}`
                            : ""
                    }`}
                    variant={statusChip(offer.kind, offer.status).variant}
                    tinted
                    truncate={false}
                />
            )}
            {offer.temporary === true && <Badge text={TEXTS.temporary} variant="warning" truncate={false} />}
        </>
    )

    return (
        <Card
            p="4"
            pressable={tappable}
            onPress={tappable ? () => onPress?.(offer) : undefined}
            leftBorderColor={pending ? "role" : undefined}
            testID={testID}
        >
            <Stack gap="2">
                <Row gap="2" justify="between" align="center" style={styles.wrap}>
                    <Text size="text-sm" color="text-muted">
                        {offer.createdLabel}
                    </Text>
                    <Row gap="1" style={styles.wrap}>
                        {chips}
                    </Row>
                </Row>
                <Text size="text-lg" weight="bold" color={pending ? "text-primary" : "text-muted"}>
                    {offer.title}
                </Text>
                <Stack gap="1">
                    {offer.lines.map((line) => (
                        <TutorOfferLineText key={line.text} line={line} muted size="text-md" />
                    ))}
                </Stack>
            </Stack>
        </Card>
    )
}

const styles = StyleSheet.create(() => ({
    wrap: { flexWrap: "wrap" },
}))
